import { i as formatPrice } from "@theme/utils";
import { r as reconcileTrialPlaceholder } from "@theme/trial-placeholder";
import { t as gsapWithCSS } from "@theme/gsap";
import { t as createQuizTracker } from "@theme/quiz-analytics";
import { n as getVariantData, r as submitEmail, t as getSellingPlanNameFromDOM } from "@theme/submit-quiz-email";
//#region frontend/lib/custom-select.js
function setupSelectBehavior(select, chooseFn) {
	const trigger = select.querySelector(".pdp-select__trigger");
	const list = select.querySelector(".pdp-select__list");
	const options = [...select.querySelectorAll(".pdp-select__option")];
	let focusedIndex = -1;
	const setFocus = (index) => {
		focusedIndex = index;
		options.forEach((o) => o.classList.remove("is-focused"));
		if (index >= 0 && index < options.length) {
			options[index].classList.add("is-focused");
			options[index].scrollIntoView({ block: "nearest" });
			options[index].focus();
		}
	};
	const open = () => {
		select.classList.add("is-open");
		trigger.setAttribute("aria-expanded", "true");
		const selectedIdx = options.findIndex((o) => o.classList.contains("is-selected") || o.getAttribute("aria-selected") === "true");
		setFocus(selectedIdx >= 0 ? selectedIdx : 0);
	};
	const close = () => {
		select.classList.remove("is-open");
		trigger.setAttribute("aria-expanded", "false");
		options.forEach((o) => o.classList.remove("is-focused"));
		focusedIndex = -1;
	};
	const choose = (option) => {
		chooseFn(option);
		close();
		trigger.focus();
	};
	trigger.addEventListener("click", () => {
		select.classList.contains("is-open") ? close() : open();
	});
	trigger.addEventListener("keydown", (e) => {
		if ([
			"ArrowDown",
			"Enter",
			" "
		].includes(e.key)) {
			e.preventDefault();
			if (!select.classList.contains("is-open")) open();
			else if (e.key === "ArrowDown") setFocus(Math.min(focusedIndex + 1, options.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setFocus(Math.max(focusedIndex - 1, 0));
		} else if (e.key === "Escape") close();
	});
	list.addEventListener("keydown", (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setFocus(Math.min(focusedIndex + 1, options.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setFocus(Math.max(focusedIndex - 1, 0));
		} else if (["Enter", " "].includes(e.key)) {
			e.preventDefault();
			if (focusedIndex >= 0) choose(options[focusedIndex]);
		} else if (e.key === "Escape") {
			close();
			trigger.focus();
		}
	});
	options.forEach((option, index) => {
		option.addEventListener("click", () => choose(option));
		option.addEventListener("mouseenter", () => setFocus(index));
	});
	document.addEventListener("click", (e) => {
		if (!select.contains(e.target)) close();
	});
	select.addEventListener("focusout", (e) => {
		if (!select.contains(e.relatedTarget)) close();
	});
}
//#endregion
//#region frontend/islands/pdp-quiz-logic.js
var PDPQuizLogic = class extends HTMLElement {
	connectedCallback() {
		this.tabs = this.querySelector("#pdp-quiz-tabs");
		this.chooseTab = this.tabs?.querySelector("#tab-pick");
		this.chooseForm = this.querySelector("#pdp-choose-form");
		this.quizTab = this.tabs?.querySelector("#tab-quiz");
		this.quiz = this.querySelector("#pdp-quiz-form");
		this.planAndButtons = this.querySelector("#pdp-plan-and-buttons");
		this.answers = {};
		this.mealOptions = {};
		this.currentStep = 1;
		this.userChangedRecipe = false;
		this.analytics = createQuizTracker("pdp");
		this.trialEnabled = this.hasAttribute("data-trial-enabled");
		this.purchaseType = "subscribe";
		this._purchaseChosen = false;
		this._trialRecipeOverridden = false;
		this.scrollToTopOnResults = !this.hasAttribute("data-no-scroll-top");
		this.productUrl = this.dataset.productUrl;
		this.quizOnly = document.documentElement.classList.contains("is-quiz-only");
		this.addEventListener("input", (e) => {
			if (!e.target.closest("input")) return;
			if (e.target.name?.startsWith("pdp_quiz_")) this.analytics.start();
			this.answers[e.target.name] = e.target.value;
			this._syncCartAttributes();
		});
		if (this.tabs) {
			this.chooseTab.addEventListener("click", () => this.toggleTab(this.quizTab, this.chooseTab));
			this.quizTab.addEventListener("click", () => {
				this.analytics.start();
				this.toggleTab(this.chooseTab, this.quizTab);
			});
			this.tabs.addEventListener("keydown", (e) => {
				if (![
					"ArrowLeft",
					"ArrowRight",
					"Home",
					"End"
				].includes(e.key)) return;
				e.preventDefault();
				const next = e.key === "Home" ? this.chooseTab : e.key === "End" ? this.quizTab : document.activeElement === this.chooseTab ? this.quizTab : this.chooseTab;
				next.focus();
				next.click();
			});
			const _initParams = new URLSearchParams(window.location.search);
			const _isCustomized = _initParams.has("name") && _initParams.has("weight");
			if (_initParams.get("tab") === "quiz" && !_isCustomized) this.toggleTab(this.chooseTab, this.quizTab);
		}
		const planAddButton = this.querySelector("[data-plan-add-to-cart]");
		if (planAddButton) planAddButton.addEventListener("click", () => this._addPlanToCart());
		this.submitBtn = this.querySelector("#view-meal-options");
		this.submitBtn.addEventListener("click", () => this.navigateToMealOptions());
		this.querySelector("form").addEventListener("submit", (e) => this.getQuizResults(e));
		this.querySelectorAll("#pdp-quiz-form input").forEach((input) => {
			input.value = "";
		});
		const params = new URLSearchParams(window.location.search);
		const isCustomizedView = params.has("name") && params.has("weight");
		const source = params.get("quiz_source");
		this.quizSource = ["standalone", "calculator"].includes(source) ? source : "pdp";
		const via = params.get("plan_via");
		this.planVia = ["email"].includes(via) ? via : null;
		if (isCustomizedView) {
			const params = new URLSearchParams(window.location.search);
			const name = params.get("name");
			const weight = params.get("weight");
			const condition = params.get("condition") || "NONE";
			const breed = params.get("breed");
			if (name) this.answers.pdp_quiz_dogs_name = name;
			if (weight) this.answers.pdp_quiz_dogs_weight = weight;
			this.answers.pdp_quiz_dogs_condition = condition;
			if (breed) this.answers.pdp_quiz_dogs_breed = breed;
			if (params.get("from_quiz") === "1" && sessionStorage.getItem("from_quiz") === "1") {
				sessionStorage.removeItem("from_quiz");
				const mealKey = (params.get("meal") || "full").toLowerCase();
				try {
					const stored = sessionStorage.getItem("quiz_meal_options");
					if (stored) this.mealOptions = JSON.parse(stored);
				} catch {
					console.log("error getting meal options from session storage");
				}
				sessionStorage.removeItem("quiz_meal_options");
				Promise.all([
					customElements.whenDefined("variant-radios"),
					customElements.whenDefined("purchase-options"),
					customElements.whenDefined("product-media-gallery"),
					customElements.whenDefined("product-recipe-picker"),
					customElements.whenDefined("product-alternatives-drawer")
				]).then(() => {
					this._applyMealOptionsToUI(mealKey, { applySelection: true });
					document.documentElement.classList.remove("results-pending");
				});
			} else if (weight) {
				const apiParams = new URLSearchParams({
					weight,
					condition
				});
				fetch(`https://happy-howl-app.vercel.app/api/meals?${apiParams}`).then((r) => r.json()).then((data) => {
					if (data.error) return;
					this.mealOptions = data;
					Promise.all([
						customElements.whenDefined("variant-radios"),
						customElements.whenDefined("purchase-options"),
						customElements.whenDefined("product-media-gallery"),
						customElements.whenDefined("product-recipe-picker"),
						customElements.whenDefined("product-alternatives-drawer")
					]).then(() => {
						this._applyMealOptionsToUI((params.get("meal") || "full").toLowerCase(), { applySelection: true });
						document.documentElement.classList.remove("results-pending");
					});
				}).catch(() => {});
			}
		}
		this._syncCartAttributes();
		this._populateMealOptionData();
		this.initSelects();
		this.initMealSelects();
		const initialMeal = new URLSearchParams(window.location.search).get("meal") || "full";
		this._updatePlanStats(initialMeal);
		if (this.trialEnabled) this._initTrial();
		document.addEventListener("recipe:change", (evt) => {
			if (evt.detail.variant?.options) this.userChangedRecipe = true;
			if (this.trialEnabled && !evt.detail.programmatic) {
				this._trialRecipeOverridden = true;
				const code = this._variantData()?.[String(evt.detail.variant?.id)]?.recipeCode;
				if (code) this._refetchTrialForRecipe(code);
			}
			const variant = evt.detail.variant;
			if (!variant) return;
			const variantInfo = this._variantData()[variant.id];
			if (!variantInfo) return;
			const img = this.querySelector("#plan-variant-image");
			const title = this.querySelector("#plan-variant-title");
			const bestFor = this.querySelector("#plan-variant-best-for");
			if (img) {
				img.src = variantInfo.image;
				img.alt = variantInfo.imageAlt;
			}
			if (title) title.textContent = variantInfo.title;
			if (bestFor && variantInfo.bestFor) {
				const checkSvg = `<svg width="8" height="6" viewBox="0 0 8 6" aria-hidden="true" focusable="false" fill="none" class="[&>*]:stroke-[1px] text-green h-[12px] w-[12px]">
          <path d="M0.75 3L2.57609 4.75L6.75 0.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
				bestFor.innerHTML = variantInfo.bestFor.map((item) => `<li class="mb-gutter-xs gap-x-gutter-sm flex items-center justify-center md:justify-start">${checkSvg}${item}</li>`).join("");
			}
			if (evt.detail.recipeName && Object.keys(this.mealOptions).length) this._updateMealOptionsForRecipe(evt.detail.recipeName, evt.detail.variant);
		});
		document.addEventListener("change", (e) => {
			if (!e.target.matches("purchase-options input[data-radio-type]")) return;
			const mealKey = this.querySelector("[data-pdp-meal-select]")?.dataset.selected || "full";
			this._updatePlanStats(mealKey);
		});
	}
	_variantData() {
		try {
			return JSON.parse(document.getElementById("plan-variant-data")?.textContent || "{}");
		} catch {
			return;
		}
	}
	initSelects() {
		this.querySelectorAll("[data-pdp-select]").forEach((select) => {
			const input = select.querySelector("input[type='hidden']");
			const label = select.querySelector(".pdp-select__label");
			const options = [...select.querySelectorAll(".pdp-select__option")];
			setupSelectBehavior(select, (option) => {
				input.value = option.dataset.value;
				label.textContent = option.textContent;
				select.dataset.selected = option.dataset.value;
				options.forEach((o) => o.removeAttribute("aria-selected"));
				option.setAttribute("aria-selected", "true");
				if (input.name) {
					this.answers[input.name] = option.dataset.value;
					this._syncCartAttributes();
				}
			});
		});
	}
	initMealSelects() {
		this.querySelectorAll("[data-pdp-meal-select]").forEach((group) => {
			const options = [...group.querySelectorAll(".pdp-meal-select__option")];
			const choose = (option) => {
				if (group.dataset.selected === option.dataset.value) return;
				this._markMealSelected(option.dataset.value);
				this._applyMealSelection(option.dataset.value);
			};
			options.forEach((option) => option.addEventListener("click", () => choose(option)));
			group.addEventListener("keydown", (e) => {
				const step = {
					ArrowRight: 1,
					ArrowDown: 1,
					ArrowLeft: -1,
					ArrowUp: -1
				}[e.key];
				if (!step) return;
				e.preventDefault();
				const next = options[(options.indexOf(document.activeElement) + step + options.length) % options.length];
				next.focus();
				choose(next);
			});
			const mealParam = new URLSearchParams(window.location.search).get("meal");
			const initial = mealParam && options.find((o) => o.dataset.value.toLowerCase() === mealParam.toLowerCase()) || options[0];
			if (initial) this._markMealSelected(initial.dataset.value);
		});
	}
	_markMealSelected(mealKey) {
		const group = this.querySelector("[data-pdp-meal-select]");
		if (!group) return;
		group.dataset.selected = mealKey;
		group.querySelectorAll(".pdp-meal-select__option").forEach((o) => {
			const selected = o.dataset.value === mealKey;
			o.classList.toggle("is-selected", selected);
			o.setAttribute("aria-checked", String(selected));
			o.tabIndex = selected ? 0 : -1;
		});
	}
	toggleTab(prev, curr) {
		prev.classList.remove("active");
		prev.setAttribute("aria-selected", "false");
		prev.tabIndex = -1;
		curr.classList.add("active");
		curr.setAttribute("aria-selected", "true");
		curr.tabIndex = 0;
		if (curr.id === "tab-quiz") {
			this.mealOptions = {};
			this.answers = {};
			this.quiz.style.display = "flex";
			this.chooseForm.style.display = "none";
			if (this.planAndButtons) this.planAndButtons.style.display = "none";
		} else {
			this.quiz.style.display = "none";
			this.chooseForm.style.display = "flex";
			if (this.planAndButtons) this.planAndButtons.style.display = "";
		}
	}
	getQuizResults(e) {
		e.preventDefault();
		this.navigateToMealOptions();
	}
	goToStep(step) {
		const prev = this.querySelector("[data-embedded-quiz-step]:not([hidden])");
		this.currentStep = step;
		const show = () => {
			this.querySelectorAll("[data-embedded-quiz-step]").forEach((panel) => {
				panel.hidden = panel.dataset.embeddedQuizStep !== String(step);
			});
			const next = this.querySelector(`[data-embedded-quiz-step="${step}"]`);
			if (next) {
				next.style.opacity = "0";
				requestAnimationFrame(() => requestAnimationFrame(() => {
					next.style.opacity = "";
				}));
			}
		};
		let done = false;
		const finish = (e) => {
			if (e && (e.target !== prev || e.propertyName !== "opacity")) return;
			if (done) return;
			done = true;
			prev.removeEventListener("transitionend", finish);
			show();
		};
		prev.style.opacity = "0";
		prev.addEventListener("transitionend", finish);
		setTimeout(finish, 350);
	}
	navigateToMealOptions() {
		this.mealOptions = {};
		const base = "https://happy-howl-app.vercel.app/api/meals";
		let errors = document.querySelector("#quiz-validation-error");
		if (!this.answers.pdp_quiz_dogs_name) {
			errors.classList.remove("hidden");
			errors.innerText = "Please enter your dog's name.";
			return;
		} else if (!this.answers.pdp_quiz_dogs_weight) {
			errors.classList.remove("hidden");
			errors.innerText = "Please enter your dog's weight. If you're not certain, please give us your best estimate.";
			return;
		} else if (this.answers.pdp_quiz_dogs_weight < 0 || this.answers.pdp_quiz_dogs_weight > 115) {
			errors.classList.remove("hidden");
			errors.innerText = "We aren't able to make a recommendation based on your dog's given weight.";
			return;
		}
		if (!this.answers.pdp_quiz_dogs_condition) {
			this.answers.pdp_quiz_dogs_condition = "NONE";
			this._syncCartAttributes();
		}
		errors.classList.add("hidden");
		this.submitBtn.disabled = true;
		this.submitBtn.textContent = "Loading...";
		const reset = () => {
			this.submitBtn.disabled = false;
			this.submitBtn.textContent = "View Meal Options";
		};
		const params = new URLSearchParams({
			weight: this.answers.pdp_quiz_dogs_weight,
			condition: this.answers.pdp_quiz_dogs_condition
		});
		fetch(`${base}?${params}`).then((r) => r.json()).then((data) => {
			if (data.error) {
				this.analytics.error("meals_api_error");
				reset();
				return;
			}
			this.mealOptions = data;
			const { variant, sellingPlan } = data.full;
			const name = this.answers.pdp_quiz_dogs_name;
			const weight = this.answers.pdp_quiz_dogs_weight;
			const condition = this.answers.pdp_quiz_dogs_condition;
			const email = this.answers.pdp_quiz_user_email?.trim();
			const breed = this.answers.pdp_quiz_dogs_breed;
			const resultsParams = new URLSearchParams({
				name: name ?? "",
				weight: weight ?? "",
				condition: condition ?? "NONE",
				meal: "full",
				from_quiz: "1",
				quiz_source: "pdp",
				...breed && breed !== "skip" && { breed },
				...variant && { variant },
				...sellingPlan && { sellingPlan }
			});
			if (!(!!email && submitEmail(email, {
				name,
				weight,
				condition,
				breed,
				variantId: variant,
				sellingPlanId: sellingPlan,
				quizSource: "pdp",
				resultsPath: `${window.location.pathname}?${resultsParams}`
			}))) try {
				window.klaviyo?.track("Quiz Finished", {
					name,
					weight,
					condition
				});
			} catch (e) {
				console.error("Klaviyo tracking failed:", e);
			}
			this.analytics.complete({
				weight,
				mealSize: "full",
				conditionCode: condition,
				variantId: variant,
				variantTitle: getVariantData()?.[String(variant)]?.title,
				sellingPlanId: sellingPlan,
				sellingPlanName: getSellingPlanNameFromDOM(variant, sellingPlan)
			});
			const urlParams = new URLSearchParams(window.location.search);
			Object.entries({
				name,
				weight,
				condition,
				variant,
				sellingPlan
			}).forEach(([k, v]) => urlParams.set(k, v));
			history.pushState(null, "", `${window.location.pathname}?${urlParams}`);
			const planView = this.querySelector("#pdp-plan-view");
			if (planView) planView.style.opacity = "0";
			const top = this.scrollToTopOnResults ? 0 : this.getBoundingClientRect().top + window.scrollY - (document.querySelector("header")?.offsetHeight ?? 0);
			gsapWithCSS.to(document.documentElement, {
				scrollTop: top,
				duration: .6,
				ease: "power2.inOut"
			});
			this.quiz.style.transition = "opacity 0.25s ease";
			this.quiz.style.opacity = "0";
			let done = false;
			const showPlan = () => {
				if (done) return;
				done = true;
				this.quiz.removeEventListener("transitionend", showPlan);
				this.quiz.style.display = "none";
				this.quiz.style.transition = "";
				this.quiz.style.opacity = "";
				document.documentElement.classList.add("is-customized");
				document.documentElement.classList.remove("is-quiz-only");
				document.querySelectorAll("[data-url-param='name']").forEach((el) => {
					el.textContent = name;
				});
				if (this.planAndButtons) this.planAndButtons.style.display = "";
				Promise.all([
					customElements.whenDefined("variant-radios"),
					customElements.whenDefined("purchase-options"),
					customElements.whenDefined("product-media-gallery"),
					customElements.whenDefined("product-recipe-picker"),
					customElements.whenDefined("product-alternatives-drawer")
				]).then(() => this._applyMealOptionsToUI("full"));
				if (planView) requestAnimationFrame(() => requestAnimationFrame(() => {
					planView.style.transition = "opacity 0.3s ease";
					planView.style.opacity = "";
					planView.addEventListener("transitionend", () => {
						planView.style.transition = "";
					}, { once: true });
				}));
			};
			this.quiz.addEventListener("transitionend", showPlan);
			setTimeout(showPlan, 300);
		}).catch(() => {
			this.analytics.error("meals_fetch_failed");
			reset();
		});
	}
	_updatePlanStats(mealKey) {
		const data = this.mealOptions[mealKey];
		if (!data) return;
		const set = (attr, val) => {
			const el = this.querySelector(`[data-plan-stat="${attr}"]`);
			if (el) el.textContent = val;
		};
		const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;
		const mealCount = data.daysOfFood * 2;
		const fieldset = document.querySelector(`purchase-options fieldset[data-variant-id="${data.variant}"]`);
		const select = fieldset?.querySelector("[data-selling-plan-select]");
		const planOption = select ? select.options[select.selectedIndex] : fieldset?.querySelector(`[data-selling-plan-select] option[value="${data.sellingPlan}"]`);
		const priceCents = planOption ? Number(planOption.dataset.priceCents) : null;
		const pricePerDay = priceCents != null ? fmt(priceCents / data.daysOfFood) : "—";
		set("meal-count", `${mealCount} meals`);
		set("days-of-food", `${data.daysOfFood} days`);
		set("price-per-day", pricePerDay);
	}
	_updateMealOptionsForRecipe(recipeName, selectedVariant) {
		const variantData = this._variantData();
		const allVariants = document.querySelector("variant-radios")?.getVariantData();
		if (!allVariants) return;
		const recipeOptionIndex = selectedVariant?.options?.indexOf(recipeName) ?? -1;
		if (recipeOptionIndex === -1) return;
		const byUnits = {};
		for (const v of allVariants) {
			if (v.options[recipeOptionIndex] !== recipeName) continue;
			const units = variantData[String(v.id)]?.units;
			if (units != null) byUnits[String(units)] = v;
		}
		let changed = false;
		for (const key of Object.keys(this.mealOptions)) {
			const data = this.mealOptions[key];
			const currentUnits = variantData[String(data.variant)]?.units;
			if (currentUnits == null) continue;
			const newVariant = byUnits[String(currentUnits)];
			if (!newVariant || String(newVariant.id) === String(data.variant)) continue;
			this.mealOptions[key] = {
				...data,
				variant: String(newVariant.id),
				variantTitle: variantData[String(newVariant.id)]?.title ?? data.variantTitle
			};
			changed = true;
		}
		if (!changed) return;
		this._populateMealOptionData();
		const currentKey = this._currentMealKey();
		this._applyMealSelection(currentKey, { preservePlan: true });
		this._updatePlanStats(currentKey);
	}
	_populateMealOptionData() {
		if (!Object.keys(this.mealOptions).length) return;
		let currentRecipeVariantByUnits = null;
		if (this.userChangedRecipe) {
			const variantData = this._variantData();
			const allVariants = document.querySelector("variant-radios")?.getVariantData?.() ?? [];
			const recipePicker = document.querySelector("product-recipe-picker");
			const recipeIdx = recipePicker ? parseInt(recipePicker.dataset.optionIndex, 10) : NaN;
			if (!isNaN(recipeIdx)) {
				const currentRecipe = (document.querySelector("variant-radios")?.querySelectorAll("fieldset")?.[recipeIdx]?.querySelector("input:checked"))?.value;
				if (currentRecipe) {
					currentRecipeVariantByUnits = {};
					for (const v of allVariants) {
						if (v.options[recipeIdx] !== currentRecipe) continue;
						const units = variantData[String(v.id)]?.units;
						if (units != null) currentRecipeVariantByUnits[String(units)] = String(v.id);
					}
				}
			}
		}
		this.querySelectorAll("[data-pdp-meal-select] .pdp-meal-select__option").forEach((option) => {
			const data = this.mealOptions[option.dataset.value];
			if (!data) return;
			let targetVariantId = String(data.variant);
			if (currentRecipeVariantByUnits) {
				const units = this._variantData()[String(data.variant)]?.units;
				if (units != null) targetVariantId = currentRecipeVariantByUnits[String(units)] ?? targetVariantId;
			}
			const fieldset = document.querySelector(`purchase-options fieldset[data-variant-id="${targetVariantId}"]`);
			const planOption = this.userChangedRecipe ? fieldset?.querySelector("[data-selling-plan-select] option") : fieldset?.querySelector(`[data-selling-plan-select] option[value="${data.sellingPlan}"]`);
			const priceCents = planOption ? Number(planOption.dataset.priceCents) : null;
			const pricePerMeal = priceCents != null ? (priceCents / 100 / (data.daysOfFood * 2)).toFixed(2) : (Number(data.costPerDay) / 2).toFixed(2);
			option.dataset.price = pricePerMeal;
			const priceEl = option.querySelector(".pdp-meal-select__option-price");
			if (priceEl) priceEl.textContent = `$${pricePerMeal}/meal`;
		});
	}
	_applyMealOptionsToUI(mealKey = "full", { applySelection = true } = {}) {
		this._populateMealOptionData();
		this._markMealSelected(mealKey);
		this._updatePlanStats(mealKey);
		if (this.trialEnabled) this._updateTrialRadio(mealKey);
		if (applySelection) this._applyMealSelection(mealKey, { fromQuiz: true });
	}
	_applyMealSelection(mealKey, { preservePlan = false, fromQuiz = false } = {}) {
		const data = this.mealOptions[mealKey];
		if (!data) return;
		let currentPlanName = null;
		if (preservePlan) {
			const prevPlanId = new URLSearchParams(window.location.search).get("sellingPlan");
			if (prevPlanId) currentPlanName = document.querySelector(`purchase-options [data-selling-plan-select] option[value="${prevPlanId}"]`)?.textContent?.trim() ?? null;
		}
		const urlParams = new URLSearchParams(window.location.search);
		urlParams.set("variant", data.variant);
		if (!preservePlan) urlParams.set("sellingPlan", data.sellingPlan);
		history.replaceState(null, "", `${window.location.pathname}?${urlParams}`);
		const variantRadios = document.querySelector("variant-radios");
		const targetVariant = (variantRadios?.getVariantData?.())?.find((v) => String(v.id) === String(data.variant));
		let recipeName;
		if (variantRadios && targetVariant) {
			Array.from(variantRadios.querySelectorAll("fieldset")).forEach((fieldset, i) => {
				const val = targetVariant.options[i];
				if (!val) return;
				const radio = fieldset.querySelector(`input[type="radio"][value="${CSS.escape(val)}"]`);
				if (radio) radio.checked = true;
			});
			variantRadios.dispatchEvent(new Event("change"));
			const recipePicker = document.querySelector("product-recipe-picker");
			if (recipePicker) {
				const idx = parseInt(recipePicker.dataset.optionIndex, 10);
				if (!isNaN(idx)) recipeName = targetVariant.options[idx];
			}
		}
		this._updatePlanStats(mealKey);
		document.dispatchEvent(new CustomEvent("recipe:change", { detail: {
			variant: targetVariant ?? { id: String(data.variant) },
			recipeName,
			fromQuiz
		} }));
		requestAnimationFrame(() => {
			const po = document.querySelector("purchase-options");
			if (!po) return;
			if (currentPlanName) {
				const fieldset = po.querySelector("fieldset[data-variant-id]:not([hidden])");
				const select = fieldset?.querySelector("[data-selling-plan-select]");
				const match = select && Array.from(select.options).find((o) => o.textContent.trim() === currentPlanName);
				if (match) {
					select.value = match.value;
					const subscribeRadio = fieldset.querySelector("input[data-radio-type=\"subscribe\"]");
					if (subscribeRadio) subscribeRadio.checked = true;
					select.dispatchEvent(new Event("change", { bubbles: true }));
					po.syncFromActive?.({ animate: false });
					return;
				}
			}
			const fieldset = po.querySelector("fieldset[data-variant-id]:not([hidden])");
			if (!fieldset) return;
			const select = fieldset.querySelector("[data-selling-plan-select]");
			if (!select?.querySelector(`option[value="${data.sellingPlan}"]`)) return;
			select.value = data.sellingPlan;
			const subscribeRadio = fieldset.querySelector("input[data-radio-type=\"subscribe\"]");
			if (subscribeRadio) subscribeRadio.checked = true;
			select.dispatchEvent(new Event("change", { bubbles: true }));
			po.syncFromActive?.({ animate: false });
		});
		if (this.trialEnabled) requestAnimationFrame(() => this._updateTrialRadio(mealKey));
	}
	_syncCartAttributes() {
		const sectionId = document.querySelector("purchase-options")?.dataset.section;
		const form = sectionId ? document.getElementById(`product-form-${sectionId}`) : null;
		if (!form) return;
		const attrs = {
			"Dog Name": this.answers.pdp_quiz_dogs_name,
			"Dog Weight": this.answers.pdp_quiz_dogs_weight,
			"Dog Health Condition": this.answers.pdp_quiz_dogs_condition,
			"Dog Breed": this.answers.pdp_quiz_dogs_breed
		};
		if (Object.values(attrs).some(Boolean)) {
			attrs._quiz_source = this.quizSource;
			const session = this.analytics?.sessionId();
			if (session) attrs._quiz_session = session;
			if (this.planVia) attrs._plan_via = this.planVia;
		}
		for (const [key, value] of Object.entries(attrs)) {
			if (!value) continue;
			const name = `properties[${key}]`;
			let input = form.querySelector(`input[name="${name}"]`);
			if (!input) {
				input = document.createElement("input");
				input.type = "hidden";
				input.name = name;
				form.appendChild(input);
			}
			input.value = value;
		}
	}
	_addPlanToCart() {
		const sectionId = document.querySelector("purchase-options")?.dataset.section;
		const form = sectionId ? document.getElementById(`product-form-${sectionId}`) : null;
		if (!form) return;
		const formData = new FormData(form);
		formData.delete("sections");
		fetch(window.routes?.cart_add_url ?? "/cart/add.js", {
			method: "POST",
			headers: {
				"X-Requested-With": "XMLHttpRequest",
				Accept: "application/json"
			},
			body: formData
		}).then((r) => r.json()).then((res) => {
			if (res.status) return;
			const quizPath = this.productUrl ?? window.location.pathname;
			window.location.href = `${quizPath}?tab=quiz${this.quizOnly ? "&mode=quiz" : ""}`;
		});
	}
	_remapMealOptionsToUrlVariant(urlVariant) {
		const variantData = this._variantData();
		const allVariants = document.querySelector("variant-radios")?.getVariantData?.();
		const recipePicker = document.querySelector("product-recipe-picker");
		if (!allVariants || !recipePicker) return;
		const recipeOptionIndex = parseInt(recipePicker.dataset.optionIndex, 10);
		if (isNaN(recipeOptionIndex)) return;
		const target = allVariants.find((v) => String(v.id) === String(urlVariant));
		if (!target) return;
		const recipeName = target.options[recipeOptionIndex];
		const byUnits = {};
		for (const v of allVariants) {
			if (v.options[recipeOptionIndex] !== recipeName) continue;
			const units = variantData[String(v.id)]?.units;
			if (units != null) byUnits[String(units)] = v;
		}
		for (const key of Object.keys(this.mealOptions)) {
			const mealData = this.mealOptions[key];
			const currentUnits = variantData[String(mealData.variant)]?.units;
			if (currentUnits == null) continue;
			const newVariant = byUnits[String(currentUnits)];
			if (!newVariant) continue;
			this.mealOptions[key] = {
				...mealData,
				variant: String(newVariant.id),
				variantTitle: variantData[String(newVariant.id)]?.title ?? mealData.variantTitle
			};
		}
	}
	_initTrial() {
		document.addEventListener("submit", (e) => {
			const form = e.target;
			if (!(form instanceof HTMLFormElement) || form !== this._productForm()) return;
			if (this.purchaseType !== "trial") return;
			e.preventDefault();
			e.stopPropagation();
			this._submitTrialWithPlaceholder(form);
		}, true);
		document.addEventListener("change", (e) => {
			if (e.target.matches("purchase-options input[data-radio-type]")) {
				this._purchaseChosen = true;
				this.purchaseType = e.target.dataset.radioType;
				this._updatePlanStats(this._currentMealKey());
			}
		});
	}
	_productForm() {
		const sectionId = document.querySelector("purchase-options")?.dataset.section;
		return sectionId ? document.getElementById(`product-form-${sectionId}`) : null;
	}
	_currentMealKey() {
		return this.querySelector("[data-pdp-meal-select]")?.dataset.selected || "full";
	}
	_updateTrialRadio(mealKey = this._currentMealKey()) {
		const data = this.mealOptions[mealKey];
		if (!data) return;
		const fieldset = document.querySelector(`purchase-options fieldset[data-variant-id="${data.variant}"]`);
		const card = fieldset?.querySelector("[data-trial-card]");
		const radio = card?.querySelector("input[data-radio-type=\"trial\"]");
		if (!card || !radio) return;
		const trial = data.trial;
		if (!trial || trial.price == null || this._trialRecipeOverridden) {
			card.hidden = true;
			radio.disabled = true;
			if (radio.checked) {
				const sub = fieldset.querySelector("input[data-radio-type=\"subscribe\"]");
				if (sub) {
					radio.checked = false;
					sub.checked = true;
					sub.dispatchEvent(new Event("change", { bubbles: true }));
				}
			}
			return;
		}
		const set = (sel, val) => {
			card.querySelectorAll(sel).forEach((el) => {
				el.textContent = val;
			});
		};
		const planOption = fieldset.querySelector(`[data-selling-plan-select] option[value="${data.sellingPlan}"]`);
		const planDiscount = Number(planOption?.dataset.discount) || 0;
		const laterDiscount = Number(planOption?.dataset.discountAfter) || planDiscount;
		set("[data-trial-discount-line]", laterDiscount !== planDiscount ? `Save ${planDiscount}%\u00a0today, then ${laterDiscount}% off every order after` : `Save ${planDiscount}% on every order`);
		const subPrice = Number(trial.price) * (1 - planDiscount / 100);
		set("[data-trial-today]", `$${subPrice.toFixed(2)}`);
		const fullCents = Number(fieldset.dataset.variantPriceCents);
		set("[data-trial-strike]", Number.isNaN(fullCents) ? "" : `$${(fullCents / 100).toFixed(2)}`);
		set("[data-trial-units]", String(trial.units));
		set("[data-trial-donation]", String(trial.donationUnits));
		set("[data-trial-recurring]", this._recurringLabel(data));
		radio.dataset.trialVariant = trial.variant;
		radio.dataset.trialSellingPlan = data.sellingPlan || "";
		radio.dataset.trialPriceCents = String(Math.round(subPrice * 100));
		radio.dataset.trialDonationUnits = String(trial.donationUnits);
		if (!radio.dataset.trialSellingPlan) {
			console.error("[pdp-quiz-logic] meal option has no sellingPlan; leaving the trial card hidden", mealKey);
			card.hidden = true;
			radio.disabled = true;
			return;
		}
		radio.disabled = false;
		card.hidden = false;
		if (radio.checked) document.querySelector("purchase-options")?.updateBuyPrice?.(radio.dataset.trialPriceCents);
		if ((this.purchaseType === "trial" || !this._purchaseChosen) && !radio.checked) {
			fieldset.querySelectorAll("input[data-radio-type]:checked").forEach((r) => {
				r.checked = false;
			});
			radio.checked = true;
			this.purchaseType = "trial";
			document.querySelector("purchase-options")?.syncFromActive?.({ animate: false });
		}
	}
	_recurringPrice(data) {
		const fieldset = document.querySelector(`purchase-options fieldset[data-variant-id="${data.variant}"]`);
		const opt = fieldset?.querySelector(`[data-selling-plan-select] option[value="${data.sellingPlan}"]`);
		if (!opt) return "";
		const first = Number(opt.dataset.discount);
		const later = Number(opt.dataset.discountAfter);
		const fullCents = Number(fieldset.dataset.variantPriceCents);
		if (Number.isNaN(later) || later === first || Number.isNaN(fullCents)) return opt.dataset.price || "";
		return formatPrice(Math.round(fullCents * (1 - later / 100)));
	}
	_recurringLabel(data) {
		const pack = (data.variantTitle || "").split("/").pop().trim();
		const cadence = (data.sellingPlanTitle || "").replace(/^Delivered\s*/i, "").replace(/^\w/, (c) => c.toLowerCase());
		if (pack && cadence) return `the ${pack} ${cadence}`;
		return pack ? `the ${pack}` : cadence || "—";
	}
	_refetchTrialForRecipe(recipeCode) {
		const weight = this.answers.pdp_quiz_dogs_weight;
		const condition = this.answers.pdp_quiz_dogs_condition || "NONE";
		if (!weight) return;
		const params = new URLSearchParams({
			weight,
			condition,
			recipe: recipeCode
		});
		fetch(`https://happy-howl-app.vercel.app/api/meals?${params}`).then((r) => r.json()).then((data) => {
			if (data.error) return;
			[
				"full",
				"half",
				"quarter"
			].forEach((key) => {
				if (this.mealOptions[key] && data[key]) this.mealOptions[key].trial = data[key].trial;
			});
			this._trialRecipeOverridden = false;
			this._updateTrialRadio();
		}).catch(() => {});
	}
	_setTrialProp(form, key, value) {
		const name = `properties[${key}]`;
		let input = form.querySelector(`input[name="${name}"][data-trial-prop]`);
		if (!input) {
			input = document.createElement("input");
			input.type = "hidden";
			input.name = name;
			input.dataset.trialProp = "";
			form.appendChild(input);
		}
		input.value = value;
	}
	_applyTrialToForm(form) {
		const data = this.mealOptions[this._currentMealKey()];
		const trial = data?.trial;
		if (!trial) return false;
		const idInput = form.querySelector("[name=\"id\"]");
		const planInput = form.querySelector("[name=\"selling_plan\"]");
		this._preTrial = {
			id: idInput?.value,
			plan: planInput?.value
		};
		if (idInput) idInput.value = trial.variant;
		if (planInput) planInput.value = data.sellingPlan || "";
		this._setTrialProp(form, "_recommended_variant", `gid://shopify/ProductVariant/${data.variant}`);
		this._setTrialProp(form, "_donation_units", String(trial.donationUnits));
		this._setTrialProp(form, "_trial", "true");
		this._setTrialProp(form, "_after_trial_title", data.variantTitle || "");
		this._setTrialProp(form, "_after_trial_price", this._recurringPrice(data));
		this._setTrialProp(form, "_after_trial_cadence", (data.sellingPlanTitle || "").replace(/^Delivered\s*/i, ""));
		return true;
	}
	_restoreFormFromTrial(form) {
		const idInput = form.querySelector("[name=\"id\"]");
		const planInput = form.querySelector("[name=\"selling_plan\"]");
		if (this._preTrial) {
			if (idInput && this._preTrial.id != null) idInput.value = this._preTrial.id;
			if (planInput) planInput.value = this._preTrial.plan ?? "";
			this._preTrial = null;
		}
		form.querySelectorAll("input[data-trial-prop]").forEach((el) => el.remove());
	}
	_buildTrialItems(form) {
		this._applyTrialToForm(form);
		const trialItem = this._formToItem(form);
		this._restoreFormFromTrial(form);
		return [trialItem];
	}
	_formToItem(form) {
		const fd = new FormData(form);
		const item = {
			id: fd.get("id"),
			quantity: Number(fd.get("quantity")) || 1,
			properties: {}
		};
		const plan = fd.get("selling_plan");
		if (plan) item.selling_plan = plan;
		for (const [key, value] of fd.entries()) {
			const match = key.match(/^properties\[(.+)\]$/);
			if (match && value !== "") item.properties[match[1]] = value;
		}
		return item;
	}
	async _postCartAdd(items, { withSections }) {
		const cart = document.querySelector("cart-drawer");
		const body = { items };
		if (withSections && cart) {
			body.sections = cart.getSectionsToRender().map((section) => section.id);
			body.sections_url = window.location.pathname;
		}
		return (await fetch(window.routes?.cart_add_url ?? "/cart/add.js", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-Requested-With": "XMLHttpRequest"
			},
			body: JSON.stringify(body)
		})).json();
	}
	async _submitTrialWithPlaceholder(form) {
		const cart = document.querySelector("cart-drawer");
		const button = form.querySelector("[type=\"submit\"]") ?? this.querySelector("[name=\"add\"]");
		const error = document.querySelector("product-form [data-error-message]");
		button?.setAttribute("aria-disabled", "true");
		button?.classList.add("loading");
		if (error) error.hidden = true;
		try {
			const items = this._buildTrialItems(form);
			const res = await this._postCartAdd(items, { withSections: !!cart });
			if (res.status) {
				if (error) {
					error.textContent = res.description || res.message || "";
					error.hidden = !error.textContent;
				}
				return;
			}
			if (cart) {
				cart.classList.remove("is-empty");
				cart.renderContents(res);
			} else {
				await reconcileTrialPlaceholder();
				window.location = window.routes.cart_url;
			}
		} catch (err) {
			console.error("Trial add-to-cart failed", err);
			if (error) {
				error.textContent = "Something went wrong. Please try again.";
				error.hidden = false;
			}
		} finally {
			button?.classList.remove("loading");
			button?.removeAttribute("aria-disabled");
		}
	}
	disconnectedCallback() {}
};
window.customElements.define("pdp-quiz-logic", PDPQuizLogic);
//#endregion
